import { InputNumber as AntdInputNumber, InputNumberProps } from 'antd'
import { isNil } from 'lodash'
import cx from 'classnames'
import React from 'react'
import './styles.scss'

export type { InputNumberProps }

declare type ValueType = string | number
export interface AppInputNumberProps<T extends ValueType = ValueType> extends Omit<InputNumberProps<T>, 'bordered'> {
  alwaysShowControls?: boolean
}

/**
 * This class is a wrap around [antd InputNumber](https://ant.design/components/input-number/) component extending some of
 * the properties to follow our styling guidelines.
 *
 * Please refer to antd documentation for more details.
 */
function Input<T extends ValueType = number>(
  { className, value, onFocus, onBlur, autoFocus, alwaysShowControls, ...props }: AppInputNumberProps<T>,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const [focused, setFocused] = React.useState(autoFocus)
  const handleFocus: InputNumberProps['onFocus'] = (e) => {
    setFocused(true)
    onFocus?.(e)
  }
  const handleBlur: InputNumberProps['onBlur'] = (e) => {
    setFocused(false)
    onBlur?.(e)
  }
  return (
    <AntdInputNumber
      ref={ref}
      value={value}
      // eslint-disable-next-line jsx-a11y/no-autofocus -- Potentially a good idea to handle this
      autoFocus={autoFocus}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={cx(
        'app-input-number',
        alwaysShowControls && 'app-input-number--show-controls',
        isNil(value) && 'app-input-number--empty',
        focused && 'app-input-number--focused',
        className
      )}
      {...props}
    />
  )
}

export const InputNumber = React.forwardRef(Input)
